import SpecialOfferGenerated, { SpecialOfferGeneratedEntityManager } from './generated/SpecialOffer.generated';
import { BusinessEntityFieldType } from '@xFrame4/business/base/Constants';
import { imageInfoGraphQlDetails } from '@xFrame4/business/Constants';
import { YouTubeVideoInfo, ImageInfo } from '@xFrame4/business/Types';
import ImageGallery, { GalleryImage } from '@xFrame4/business/images/ImageGallery';
import BusinessEntityWithOrderNoHelper from '@xFrame4/business/base/BusinessEntityWithOrderNoHelper';
import { SpecialOfferTranslation } from './SpecialOfferTranslation';
import { BusinessEntityFilter } from '@xFrame4/business/base/BusinessEntity';


export class SpecialOfferEntityManager extends SpecialOfferGeneratedEntityManager<SpecialOfferTranslation, SpecialOffer>
{
    constructor()
    {
        super({
            createEntity: () => new SpecialOffer(),
            fields: [
                { name: 'galleryImages', type: BusinessEntityFieldType.Custom, isInput: false, customGraphQl: ImageGallery.graphQlGalleryImageFieldDetails },
            ]
        });
    }
}


export class SpecialOffer extends SpecialOfferGenerated<SpecialOfferTranslation>
{
    static _manager: SpecialOfferEntityManager;
    static get manager(): SpecialOfferEntityManager
    {
        if (!this._manager) this._manager = new SpecialOfferEntityManager();
        return this._manager;
    }

    galleryImages: GalleryImage[] = [];
    imageGallery: ImageGallery;

    constructor()
    {
        super();

        this.imageGallery = new ImageGallery(this, 'SpecialOfferImageGallery');
    }

    // Override
    copyFromGraphQL(graphQlObject: any)
    {
        super.copyFromGraphQL(graphQlObject);

        // Load gallery images
        if (graphQlObject.galleryImages != null)
        {
            this.imageGallery.loadImagesFromGraphQl(graphQlObject.galleryImages);
        }
    }

    // Optional: override the toJson method to skip the gallery images by setting the skipFields property (it's an array of field names)
    // This is useful when working Next.js (cannot serialize the gallery images)
    toJson()
    {
        return super.toJson(['imageGallery']);
    }

    /**
     * Move the special offer to a new position.
     * 
     * @param targetObject The target object to move the special offer to.
     * @param before If true, the special offer will be moved before the target object. If false, the special offer will be moved after the target object.
     */
    async moveTo(targetObject: SpecialOffer, before: boolean = true)
    {
        return BusinessEntityWithOrderNoHelper.moveTo('SpecialOffer', this, targetObject, before);
    }

    /**
     * Get a very light version of a special offer for navigation and routing.
     * 
     * @param language The translation language
     * @param slug The slug for the language
     */
    static async getVeryLightForNavigation(language: string, slug: string)
    {
        let query = `
            id
            translations {
                language
                slug
                name
                description
                seoTitle
                seoDescription
                seoKeywords
            }
        `;

        let result = await this.manager.loadCustom(query, {
            translation_Language: language,
            translation_Slug: slug
        });

        return result?.entities[0] as SpecialOffer | null;
    }
}


export default SpecialOffer;